import React from "react";
import classNames from "classnames";

import { ScreenWatcher } from "@common/screenWatcher";
import { LayoutWatcher } from "@common/layoutWatcher";
import { ActivityWatcher } from "@common/activityWatcher";
import { Props, Option } from "./types";
import styles from "./styles.scss";

function useSelect() {
  const [isOpen, setIsOpen] = React.useState(false);
  const toggleIsOpen = () => setIsOpen(!isOpen);

  return {
    isOpen,
    toggle: toggleIsOpen,
  };
}

function SelectList<Item extends Option>(props: Props<Item>) {
  const { isOpen, toggle } = useSelect();
  const hasSelectedItems = props.selectedItems.length > 0;
  const swapSelectedItems = (item: Item) =>
    props.items.find(({ uuid }) => uuid === item.uuid) || item;
  const isItemSelected = (item: Item) =>
    !!props.selectedItems.find(({ uuid }) => item.uuid === uuid);
  const addItem = (item: Item) => {
    props.selectItem([...props.selectedItems, item]);
    (props.items.every(({ uuid }) =>
      [...props.selectedItems, item].find((item) => item.uuid === uuid),
    ) ||
      !props.multiple) &&
      toggle();
  };
  const removeItem = (item: Item) => {
    props.selectItem(
      props.selectedItems.filter(({ uuid }) => uuid !== item.uuid),
    );
    !props.multiple && toggle();
  };
  const dispatchChange = (item: Item) => () =>
    isItemSelected(item) ? removeItem(item) : addItem(item);

  return (
    <ActivityWatcher blur={() => isOpen && toggle()}>
      <div
        className={classNames(styles.selectList, {
          [styles.multiple]: props.multiple,
          [styles.opened]: isOpen,
          [styles.invalid]: props.isInvalid,
        })}
      >
        <div className={styles.input} onClick={toggle}>
          {hasSelectedItems ? (
            <ul className={styles.selectedItems}>
              {props.selectedItems.map(swapSelectedItems).map((item) => (
                <li key={item.uuid} className={styles.selectedItem}>
                  {item.name}
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.placeholder}>{props.placeholder}</div>
          )}
          <div className={styles.iconBox}>
            <p className={styles.icon}>▲</p>
          </div>
        </div>
        {isOpen && (
          <ScreenWatcher
            renderChildren={({ height }) => (
              <LayoutWatcher
                renderChildren={({ top }) => {
                  const maxHeight = height - top - 60;

                  return (
                    <ul
                      className={styles.list}
                      style={{
                        maxHeight: `${
                          maxHeight < 320 && maxHeight > 100 ? maxHeight : 320
                        }px`,
                      }}
                    >
                      {props.items.map((item) => (
                        <li
                          key={item.uuid}
                          className={classNames(styles.item, {
                            [styles.selectedListItem]: isItemSelected(item),
                          })}
                          onClick={dispatchChange(item)}
                        >
                          {item.name}
                          {props.multiple && (
                            <span className={styles.checkbox} />
                          )}
                        </li>
                      ))}
                    </ul>
                  );
                }}
              />
            )}
          />
        )}
      </div>
    </ActivityWatcher>
  );
}

export { SelectList };
